import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const categorySlug = article
    ? categories.find((c) => c.id === article.categoryId)?.slug
    : undefined;

  if (!article) {
    return (
      <>
        <SEO title="文章未找到" description="抱歉，您查找的文章不存在。" />
        <ScrollToTop />
        <Navbar />
        <main className="min-h-[60vh] pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-500 mb-8">抱歉，您查找的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categoryId === article.categoryId)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    url: `https://8x8xpeach.com/article/${article.slug}`,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '8x8x水蜜桃',
    },
  };

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://8x8xpeach.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.image}
        schema={[articleSchema]}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6 pb-20">
          <div className="py-8">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: article.categoryName, path: categorySlug ? `/category/${categorySlug}` : undefined },
                { label: article.title },
              ]}
            />

            <div className="mt-4">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full mb-4">
                {article.categoryName}
              </span>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-snug mb-4">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  阅读时间 {article.readTime}
                </span>
              </div>

              <div className="mb-10 rounded-2xl overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-auto max-h-[450px] object-cover"
                  title={article.title}
                />
              </div>

              <div
                className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-900 prose-p:text-foreground-700 prose-p:leading-relaxed prose-strong:text-primary-700 prose-a:text-primary-600"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-16">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-2xl overflow-hidden hover:shadow-md transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        title={ra.title}
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1.5">
                        {ra.title}
                      </h3>
                      <span className="text-xs text-foreground-400">{ra.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}